import { origineDesinscription } from './communications.js'

/**
 * Constantes liées aux formateurs
 * Source de vérité unique — miroir du schéma Prisma
 */

export const FormateurType = {
  INDEPENDANT: 'independant',
  ORGANISME: 'organisme',
}

export const FORMATEUR_TYPE_LABELS = {
  [FormateurType.INDEPENDANT]: 'Indépendant',
  [FormateurType.ORGANISME]: 'Organisme',
}

export const FormateurStatut = {
  EN_ATTENTE: 'en_attente',
  VALIDE: 'valide',
  // Fiche supprimée du back-office. Réversible : elle se restaure, et son
  // propriétaire peut de lui-même se réinscrire avec la même adresse email.
  CORBEILLE: 'corbeille',
}

// Réexporté depuis shared/ : le front et le serveur doivent dire « inscription
// en cours » des mêmes fiches (cf. shared/formateur-inscription.js).
export { STATUTS_INSCRIPTION_EN_COURS as FORMATEUR_STATUTS_INSCRIPTION_EN_COURS } from '../../shared/formateur-inscription.js'

/**
 * Les réseaux d'appartenance, et les options du filtre de la liste.
 *
 * Depuis la scission du 27/08/2026, un formateur appartient à Alertis, à Global
 * Prévention, ou aux deux. Le filtre ne pose donc qu'une seule question — « qui
 * est dans ce réseau ? » — et celui des deux côtés répond oui aux deux : pas de
 * croisement « seulement » ni de « les deux », qui demandaient au lecteur de
 * tenir six cas en tête pour en utiliser deux.
 */
export const FORMATEUR_RESEAU_LABELS = {
  alertis: 'Alertis',
  global_prevention: 'Global Prévention',
}

export const FORMATEUR_RESEAU_FILTER_OPTIONS = [
  { value: '', label: 'Tous' },
  { value: 'alertis', label: 'Alertis' },
  { value: 'global_prevention', label: 'Global Prévention' },
]

/**
 * « Déjà travaillé avec Alertis » : au moins une mission dans l'historique de la
 * fiche (saisie, reprise du classeur ou attribuée dans le back-office), quelle
 * que soit l'année. Règle serveur : server/utils/formateur-deja-travaille.js.
 */
export const FORMATEUR_DEJA_TRAVAILLE_FILTER_OPTIONS = [
  { value: '', label: 'Tous' },
  { value: 'oui', label: 'Oui' },
  { value: 'non', label: 'Non' },
]

/**
 * Comment se lit une ligne de la corbeille — une étiquette (au singulier, pour
 * la ligne, et au pluriel, pour le filtre), sa couleur, la date qui la date et
 * le champ qui porte sa justification.
 *
 * Trois populations qui n'ont en commun que d'être SORTIES de la liste, pour
 * trois raisons qu'il ne faut surtout pas confondre :
 *
 *   · Supprimé   — la fiche a été mise à la corbeille. Réversible.
 *   · Inactif    — il ne fait plus partie du processus. Aucune sanction : il
 *                  ne reçoit simplement plus de campagne ni de relance, et
 *                  garde l'accès à son espace.
 *   · Blacklisté — décision commerciale : on ne le missionne plus.
 *
 * L'ORDRE COMPTE : c'est celui dans lequel on cherche l'étiquette à afficher
 * sur une ligne. Une fiche supprimée est supprimée, même si elle était
 * inactive avant — dire « Inactif » d'une fiche qu'on vient de mettre à la
 * corbeille désignerait le mauvais geste, et surtout le mauvais bouton pour
 * le défaire.
 *
 * Seule source de vérité des trois motifs : `FORMATEUR_POUBELLE_MOTIF_OPTIONS`
 * (le filtre) en dérive plutôt que de les réénumérer, pour qu'un quatrième
 * motif ou un renommage n'ait qu'un seul endroit à toucher.
 */
export const FORMATEUR_POUBELLE_SITUATIONS = [
  // AVANT « Supprimé » : c'est une suppression, mais faite par le formateur
  // lui-même depuis le lien du rappel mensuel — la ligne doit le dire, pour
  // qu'on ne le restaure pas comme une erreur de manipulation.
  //
  // « Désinscrit mail / SMS » (Hugo, 05/10) : son adresse s'est désinscrite de
  // tout — par le lien d'un e-mail, sa fiche, un « Ne pas relancer » — ou il
  // s'est retiré lui-même. Il ne reçoit plus ni e-mail ni SMS, et ne revient
  // dans la liste qu'en étant autorisé de nouveau depuis sa fiche.
  {
    motif: 'desinscrit',
    description: 'Il ne reçoit plus aucun e-mail ni SMS d’Alertis, à sa demande. Il revient dans la liste si on l’autorise de nouveau depuis sa fiche. S’il s’était retiré lui-même, sa fiche reste ensuite à la corbeille : un administrateur la restaure.',
    label: 'Désinscrit mail / SMS',
    labelPluriel: 'Désinscrits mail / SMS',
    variant: 'danger',
    icon: 'fas fa-bell-slash',
    champDate: 'communicationsDesinscritesAt',
    champRaison: null,
    raisonDe: (f) => (f?.communicationsDesinscrites
      ? origineDesinscription(f.communicationsDesinscritesOrigine)
      : 'S’est désinscrit lui-même depuis le rappel d’inscription'),
    correspond: (f) => !!f?.communicationsDesinscrites || (f?.statut === 'corbeille' && !!f?.desinscritAt),
  },
  {
    motif: 'corbeille',
    description: 'Fiche supprimée depuis le back-office. Rien n’est effacé : un administrateur peut la restaurer, et le formateur peut se réinscrire avec la même adresse.',
    label: 'Supprimé',
    labelPluriel: 'Supprimés',
    variant: 'neutral',
    icon: 'fas fa-trash-can',
    champDate: 'corbeilleAt',
    champRaison: null,
    correspond: (f) => f?.statut === 'corbeille' && !f?.desinscritAt,
  },
  {
    motif: 'blackliste',
    description: 'Décision commerciale : on ne le missionne plus. Il garde son espace formateur.',
    label: 'Blacklisté',
    labelPluriel: 'Blacklistés',
    variant: 'danger',
    icon: 'fas fa-ban',
    champDate: 'blacklisteDate',
    champRaison: 'blacklisteRaison',
    correspond: (f) => !!f?.blackliste,
  },
  {
    motif: 'inactif',
    description: 'Il ne fait plus partie du processus : plus aucune campagne ni relance. Aucune sanction, il garde l’accès à son espace.',
    label: 'Inactif',
    labelPluriel: 'Inactifs',
    variant: 'warning',
    icon: 'fas fa-user-slash',
    champDate: 'inactifDepuis',
    champRaison: 'inactifRaison',
    correspond: (f) => f?.actif === false,
  },
  // LES DEUX POPULATIONS DE LA CAMPAGNE (cf. CLAUSE_CAMPAGNE_* côté serveur).
  // Après « Inactif » : une fiche du classeur donnée inactive garde son
  // étiquette d'inactive, le motif de campagne ne la prend que si on le filtre.
  {
    motif: 'campagne_sans_reponse',
    description: 'Invité par la campagne, il a reçu toutes les relances sans jamais revenir : à reprendre au téléphone.',
    label: 'Campagne : sans réponse',
    labelPluriel: 'Campagne : sans réponse',
    variant: 'danger',
    icon: 'fas fa-phone-slash',
    champDate: 'campagneEpuiseeAt',
    champRaison: null,
    raisonDe: () => 'Toutes les relances envoyées, jamais revenue — à reprendre au téléphone',
    correspond: (f) => f?.statut === 'invite' && !!f?.campagneEpuiseeAt && f?.actif !== false,
  },
  {
    motif: 'campagne_injoignable',
    description: 'Fiche du classeur que la campagne ne peut pas joindre : sans adresse réelle, désinscrite du mailing ou inactive au classeur.',
    label: 'Campagne : injoignable',
    labelPluriel: 'Campagne : injoignables',
    variant: 'neutral',
    icon: 'fas fa-link-slash',
    champDate: null,
    champRaison: null,
    // Même ordre de priorité que `etatDeLaFiche` côté serveur.
    raisonDe: (f) => {
      if (typeof f?.email === 'string' && f.email.endsWith('@import.alertis.local')) return 'Sans adresse email réelle dans le classeur'
      if (f?.importDesinscritMailing === true) return 'Désinscrit du mailing dans le classeur'
      if (f?.actif === false || f?.importActif === false) return 'Inactif au classeur'
      return 'Hors campagne : la colonne Actif du classeur était vide ou illisible'
    },
    correspond: (f) => f?.statut === 'invite'
      && (f?.actif === false || f?.importCampagneEligible !== true
        || (typeof f?.email === 'string' && f.email.endsWith('@import.alertis.local'))),
  },
]

/**
 * Les options du filtre de la corbeille — dérivées de
 * `FORMATEUR_POUBELLE_SITUATIONS` pour ne jamais diverger des trois motifs et
 * de leurs libellés au pluriel.
 *
 * L'ordre ici est celui de l'AFFICHAGE dans le filtre, pas celui de résolution
 * de l'étiquette d'une ligne ci-dessus : il suit `FORMATEUR_POUBELLE_MOTIFS`
 * côté serveur (`formateur.repository.js`), pas la priorité corbeille >
 * blackliste > inactif. On l'énumère donc explicitement plutôt que de
 * réutiliser tel quel l'ordre de `FORMATEUR_POUBELLE_SITUATIONS`.
 *
 * Le filtre est INCLUSIF : un blacklisté devenu inactif répond aux deux et
 * apparaît sous les deux. Chercher quelqu'un et ne pas le trouver parce qu'on
 * a choisi la mauvaise des deux étiquettes qu'il porte serait pire que de le
 * voir deux fois.
 */
export const FORMATEUR_POUBELLE_MOTIF_OPTIONS = [
  { value: '', label: 'Tous' },
  ...['corbeille', 'desinscrit', 'inactif', 'blackliste', 'campagne_injoignable', 'campagne_sans_reponse'].map((motif) => {
    const situation = FORMATEUR_POUBELLE_SITUATIONS.find((s) => s.motif === motif)
    return { value: situation.motif, label: situation.labelPluriel }
  }),
]

/**
 * Une pastille PAR RÉSEAU, jamais une étiquette « Les deux » : un formateur des
 * deux côtés porte les deux pastilles, et chaque réseau garde sa couleur qu'il
 * soit seul ou accompagné (cf. `FormateurReseauBadge.vue`).
 */
export const FORMATEUR_RESEAU_BADGE_CONFIG = {
  alertis: { label: 'Alertis', bg: '#fef2f2', color: '#b91c1c', border: '#fca5a5' },
  global_prevention: { label: 'Global Prévention', bg: '#ecfdf5', color: '#047857', border: '#6ee7b7' },
}

export const FORMATEUR_STATUT_LABELS = {
  [FormateurStatut.EN_ATTENTE]: 'Inscription en cours',
  [FormateurStatut.VALIDE]: 'Validé',
}

/**
 * Les options du filtre « Statut » de la liste — où en est l'inscription, et
 * RIEN D'AUTRE.
 *
 * La corbeille en est sortie, et c'est le point. Elle y avait été mise faute
 * d'un autre endroit où la loger, ce qui plaçait dans une même liste déroulante
 * deux questions sans rapport : « où en est son inscription ? » et « me
 * montres-tu ce qui a été écarté ? ». On pouvait alors croiser « Corbeille »
 * avec le filtre voisin « Inactifs » et obtenir zéro résultat sans faute de
 * personne — les deux axes ne se recoupent jamais.
 *
 * Ces deux questions vivent désormais dans le bouton Corbeille de l'en-tête,
 * qui ouvre une VUE, avec son propre filtre de motif. Ici ne restent que les
 * trois réponses que quelqu'un peut vouloir de la liste ordinaire.
 */
export const FORMATEUR_STATUT_FILTER_OPTIONS = [
  { value: '', label: 'Tous' },
  ...Object.entries(FORMATEUR_STATUT_LABELS).map(([value, label]) => ({ value, label })),
  // Pas un statut : les inscriptions arrêtées avant la dernière étape, qu'on
  // veut pouvoir isoler d'un clic (cf. FILTRE_INSCRIPTION_INACHEVEE, serveur).
  { value: 'inscription_inachevee', label: 'Inscription non finalisée' },
]

// Badge config covers legacy `rejete` records too — the value is no longer
// reachable from the registration flow but pre-existing rows in DB must still render.
export const FORMATEUR_STATUT_BADGE_CONFIG = {
  en_attente: { label: 'Inscription en cours', bg: '#fff7ed', color: '#c2410c', border: '#f59e0b' },
  valide:     { label: 'Validé',               bg: '#d1fae5', color: '#065f46', border: '#10b981' },
  rejete:     { label: 'Rejeté',               bg: '#fee2e2', color: '#991b1b', border: '#ef4444' },
  corbeille:  { label: 'Corbeille',            bg: '#f3f4f6', color: '#6b7280', border: '#9ca3af' },
  // Fiche pré-créée par import Excel, en attente de (ré)inscription via le lien email.
  invite:     { label: 'Invité (import)',      bg: '#eef2ff', color: '#3730a3', border: '#6366f1' },
}

// Genre du formateur indépendant — miroir des valeurs stockées en base
export const FORMATEUR_GENRE_OPTIONS = [
  { value: 'femme', label: 'Femme' },
  { value: 'homme', label: 'Homme' },
  { value: 'non_precise', label: 'Non précisé' },
]

export const FORMATEUR_GENRE_LABELS = Object.fromEntries(
  FORMATEUR_GENRE_OPTIONS.map(o => [o.value, o.label])
)

// Variante avec placeholder pour les <Select> d'édition (BO admin + portail)
export const FORMATEUR_GENRE_SELECT_OPTIONS = [
  { value: '', label: '— Sélectionner —' },
  ...FORMATEUR_GENRE_OPTIONS,
]

export const FORMATEUR_SOCIAL_FIELDS = [
  // `label` s'adresse au formateur qui remplit le formulaire, `displayLabel` à
  // l'équipe qui lit sa fiche : « Votre site internet » n'a pas de sens en lecture.
  { key: 'siteWeb',   formKey: 'siteWebUrl',   label: 'Votre site internet', displayLabel: 'Site web', icon: 'fas fa-globe',     placeholder: 'https://www.votre-site.com',      color: '#6B7280' },
  { key: 'linkedin',  formKey: 'linkedinUrl',  label: 'LinkedIn',  icon: 'fab fa-linkedin',  placeholder: 'https://www.linkedin.com/in/...',  color: '#0A66C2' },
  { key: 'facebook',  formKey: 'facebookUrl',  label: 'Facebook',  icon: 'fab fa-facebook',  placeholder: 'https://www.facebook.com/...',     color: '#1877F2' },
  { key: 'youtube',   formKey: 'youtubeUrl',   label: 'YouTube',   icon: 'fab fa-youtube',   placeholder: 'https://www.youtube.com/@...',     color: '#FF0000' },
  { key: 'instagram', formKey: 'instagramUrl', label: 'Instagram', icon: 'fab fa-instagram', placeholder: 'https://www.instagram.com/...',    color: '#E4405F' },
  { key: 'tiktok',    formKey: 'tiktokUrl',    label: 'TikTok',    icon: 'fab fa-tiktok',    placeholder: 'https://www.tiktok.com/@...',      color: '#000000' },
]

export const FORMATEUR_SOCIAL_FORM_KEYS = FORMATEUR_SOCIAL_FIELDS.map(f => f.formKey)

/**
 * La borne de saisie que le serveur applique déjà : `numero_forprev` est un
 * VARCHAR(50) en base.
 *
 * Elle est ici et non recopiée dans chaque écran : les champs qui la portent —
 * fiche du back-office, « Mon profil » du portail, formulaire d'inscription —
 * doivent s'arrêter au MÊME endroit, sinon une saisie passe d'un côté et rend
 * le profil inenregistrable de l'autre.
 */
export const FORMATEUR_NUMERO_FORPREV_MAX = 50
